import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import ru from '../../pages/tv/locale/ru.json' with { type: 'json' };
import en from '../../pages/tv/locale/en.json' with { type: 'json' };
import kk from '../../pages/tv/locale/kk.json' with { type: 'json' };

export type Locale = 'kk' | 'ru' | 'en';

/** Порядок языков в переключателе на TV — казахский первым. */
export const LOCALES: readonly Locale[] = ['kk', 'ru', 'en'];

function isLocale(v: unknown): v is Locale {
  return LOCALES.includes(v as Locale);
}

const STORAGE_KEY = 'groover_locale';

const translations = { kk, ru, en } as const;

export type TranslationKey = keyof typeof ru;

type Vars = Record<string, string | number>;

/** Подстановка `{name}` в строку перевода. */
function interpolate(text: string, vars?: Vars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m));
}

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: TranslationKey, vars?: Vars) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (isLocale(stored)) return stored;
    } catch { /* ignore */ }
    return 'ru';
  });

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* ignore */ }
  }, []);

  const t = useCallback(
    (key: TranslationKey, vars?: Vars) =>
      interpolate(translations[locale][key] ?? translations.ru[key] ?? key, vars),
    [locale],
  );

  return <I18nContext.Provider value={{ locale, setLocale, t }}>{children}</I18nContext.Provider>;
}

/** Текущая локаль вне React — из того же localStorage, что и `I18nProvider`. */
export function getLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch { /* ignore */ }
  return 'ru';
}

/**
 * Перевод вне React — для императивного кода (`tvAppBootstrap`), где хуки недоступны.
 * Локаль читается из того же localStorage, что и `I18nProvider`.
 */
export function translate(key: TranslationKey, vars?: Vars): string {
  const locale = getLocale();
  return interpolate(translations[locale][key] ?? translations.ru[key] ?? key, vars);
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside I18nProvider');
  return ctx;
}
